import React, { useState, useEffect, useRef } from "react";
import {
  X,
  AlertCircle,
  Plus,
  Trash2,
  Calendar,
  Clock,
  MapPin,
  User,
  ShieldAlert,
  ChevronDown,
  Search,
  Check,
  Tag,
} from "lucide-react";
import api from "../../connector/api";

const INFRACTION_LEVELS = [
  { value: "MINOR", label: "Minor (Classroom Managed - Repeat Offense)" },
  { value: "MAJOR", label: "Major (Office Managed - Immediate Escalation)" },
];

const VIOLATION_TYPES = [
  { value: "DISRUPTIVE_BEHAVIOR", label: "Disruptive Behavior" },
  { value: "TARDINESS", label: "Tardiness / Truancy" },
  { value: "DRESS_CODE", label: "Dress Code Violation" },
  { value: "ACADEMIC_DISHONESTY", label: "Academic Dishonesty" },
  { value: "INSUBORDINATION", label: "Insubordination / Defiance" },
  { value: "TECHNOLOGY_MISUSE", label: "Technology Misuse" },
  { value: "BULLYING_HARASSMENT", label: "Bullying / Harassment" },
  { value: "OTHER", label: "Other Violation" },
];

const INTERVENTION_TYPES = [
  { value: "VERBAL_WARNING", label: "Verbal Warning/Correction" },
  { value: "SEAT_REASSIGNMENT", label: "Seat Reassignment" },
  { value: "TEACHER_CONFERENCE", label: "Private Student-Teacher Conference" },
  { value: "PARENT_CONTACT", label: "Parent Contact/Email" },
  { value: "THINK_SHEET", label: 'Behavior "Think Sheet"' },
  { value: "OTHER", label: "Other" },
];

/* Custom Dropdown Component */
const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-8 border border-zinc-300 rounded px-2 text-xs bg-white text-zinc-800 flex items-center justify-between hover:border-zinc-400 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0 ml-1" />
      </button>

      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-48 overflow-y-auto py-1 text-xs">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-zinc-100 transition-colors cursor-pointer ${
                value === opt.value
                  ? "bg-blue-50 text-blue-700 font-semibold"
                  : "text-zinc-700"
              }`}
            >
              <span className="truncate">{opt.label}</span>
              {value === opt.value && (
                <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* Custom Searchable Student Selector Component */
const StudentSearchSelector = ({
  students,
  selectedStudentId,
  onSelectStudent,
  fetchingStudents,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const selectedStudent = students.find(
    (s) => String(s.id || s.student_id) === String(selectedStudentId)
  );

  const filteredStudents = students
    .filter((student) => {
      const fullName =
        student.name ||
        `${student.first_name || ""} ${student.last_name || ""}`.trim() ||
        `${student.last_name || ""}, ${student.first_name || ""}`.trim();
      return fullName.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      const lastNameA = a.last_name || a.name || "";
      const lastNameB = b.last_name || b.name || "";
      
      const lastCompare = lastNameA.localeCompare(lastNameB, undefined, {
        sensitivity: "base",
        numeric: true,
      });

      if (lastCompare !== 0) return lastCompare;

      const firstNameA = a.first_name || "";
      const firstNameB = b.first_name || "";
      return firstNameA.localeCompare(firstNameB, undefined, {
        sensitivity: "base",
        numeric: true,
      });
    });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getStudentDisplayName = (student) => {
    if (!student) return "Select a student...";
    return (
      student.name ||
      `${student.last_name || ""}, ${student.first_name || ""}`.trim() ||
      "Unknown Student"
    );
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full p-2.5 border rounded-lg flex items-center justify-between transition-colors text-left ${
          disabled
            ? "bg-zinc-100 border-zinc-200 cursor-not-allowed opacity-80"
            : "bg-zinc-50 border-zinc-200 hover:border-zinc-300 cursor-pointer"
        }`}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <User className="w-4 h-4 text-zinc-500 shrink-0" />
          <span className="font-medium text-zinc-600 shrink-0">Student:</span>
          <span className="font-bold text-zinc-900 truncate">
            {selectedStudent
              ? getStudentDisplayName(selectedStudent)
              : "Select a student..."}
          </span>
        </div>
        {!disabled && <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0 ml-2" />}
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden flex flex-col max-h-56">
          <div className="p-2 border-b border-zinc-200 bg-zinc-50 flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
            <input
              type="text"
              placeholder="Search student by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent text-xs text-zinc-800 focus:outline-none"
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="p-0.5 text-zinc-400 hover:text-zinc-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1 py-1 text-xs">
            {fetchingStudents ? (
              <div className="p-3 text-center text-zinc-400">Loading students...</div>
            ) : filteredStudents.length === 0 ? (
              <div className="p-3 text-center text-zinc-400">No students found.</div>
            ) : (
              filteredStudents.map((student) => {
                const id = String(student.id || student.student_id);
                const isSelected = String(selectedStudentId) === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      onSelectStudent(student);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-zinc-100 transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-blue-50 text-blue-700 font-bold"
                        : "text-zinc-700"
                    }`}
                  >
                    <span className="truncate">{getStudentDisplayName(student)}</span>
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-blue-600 shrink-0 ml-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DisciplinaryReferralModal = ({
  isOpen,
  onClose,
  selectedStudent,
  selectedRecord,
  subjectAssignmentId,
  classId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [students, setStudents] = useState([]);
  const [fetchingStudents, setFetchingStudents] = useState(false);
  const [studentId, setStudentId] = useState("");

  const [dateOfIncident, setDateOfIncident] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [timeOfIncident, setTimeOfIncident] = useState(
    new Date().toTimeString().slice(0, 5)
  );
  const [location, setLocation] = useState("Classroom");
  const [infractionLevel, setInfractionLevel] = useState("MINOR");
  const [violationType, setViolationType] = useState("DISRUPTIVE_BEHAVIOR");
  const [customViolationType, setCustomViolationType] = useState("");
  const [incidentDescription, setIncidentDescription] = useState("");

  const [interventions, setInterventions] = useState([
    {
      intervention_type: "VERBAL_WARNING",
      date_attempted: new Date().toISOString().split("T")[0],
      other_details: "",
    },
  ]);

  const isEditMode = Boolean(selectedRecord);

  useEffect(() => {
    if (!isOpen) return;

    const fetchClassroomStudents = async () => {
      if (!classId) return;
      setFetchingStudents(true);
      try {
        const response = await api.get(`academic/classrooms/${classId}/students/`);
        const fetchedData = response.data.results || response.data || [];
        setStudents(fetchedData);
      } catch (err) {
        console.error("Failed to fetch students:", err);
      } finally {
        setFetchingStudents(false);
      }
    };

    fetchClassroomStudents();
  }, [isOpen, classId]);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setStudentId("");
      setIncidentDescription("");
      setDateOfIncident(new Date().toISOString().split("T")[0]);
      setTimeOfIncident(new Date().toTimeString().slice(0, 5));
      setLocation("Classroom");
      setInfractionLevel("MINOR");
      setViolationType("DISRUPTIVE_BEHAVIOR");
      setCustomViolationType("");
      setInterventions([
        {
          intervention_type: "VERBAL_WARNING",
          date_attempted: new Date().toISOString().split("T")[0],
          other_details: "",
        },
      ]);
      return;
    }

    if (selectedRecord) {
      const recordStudentId = String(
        typeof selectedRecord.student === "object"
          ? selectedRecord.student?.id
          : selectedRecord.student || selectedStudent?.id || ""
      );
      setStudentId(recordStudentId);
      setDateOfIncident(selectedRecord.date_of_incident || new Date().toISOString().split("T")[0]);
      setTimeOfIncident(selectedRecord.time_of_incident || new Date().toTimeString().slice(0, 5));
      setLocation(selectedRecord.location || "Classroom");
      setInfractionLevel(selectedRecord.infraction_level || "MINOR");
      setViolationType(selectedRecord.violation_type || "DISRUPTIVE_BEHAVIOR");
      setCustomViolationType(selectedRecord.custom_violation_type || selectedRecord.other_violation_details || "");
      setIncidentDescription(selectedRecord.incident_description || "");

      if (Array.isArray(selectedRecord.interventions) && selectedRecord.interventions.length > 0) {
        setInterventions(
          selectedRecord.interventions.map((item) => ({
            intervention_type: item.intervention_type || "VERBAL_WARNING",
            date_attempted: item.date_attempted || item.intervention_date || new Date().toISOString().split("T")[0],
            other_details: item.other_details || "",
          }))
        );
      } else if (selectedRecord.intervention_type) {
        setInterventions([
          {
            intervention_type: selectedRecord.intervention_type,
            date_attempted: selectedRecord.intervention_date || new Date().toISOString().split("T")[0],
            other_details: selectedRecord.other_details || "",
          },
        ]);
      }
    } else if (selectedStudent) {
      const id = String(selectedStudent.id || selectedStudent.student_id);
      setStudentId(id);
      setStudents((prev) => {
        const exists = prev.some((s) => String(s.id || s.student_id) === id);
        return exists ? prev : [selectedStudent, ...prev];
      });
    }
  }, [isOpen, selectedStudent, selectedRecord]);

  if (!isOpen) return null;

  const handleSelectStudent = (student) => {
    setStudentId(String(student.id || student.student_id));
  };

  const handleAddIntervention = () => {
    setInterventions([
      ...interventions,
      {
        intervention_type: "VERBAL_WARNING",
        date_attempted: new Date().toISOString().split("T")[0],
        other_details: "",
      },
    ]);
  };

  const handleRemoveIntervention = (index) => {
    setInterventions(interventions.filter((_, idx) => idx !== index));
  };

  const handleInterventionChange = (index, field, value) => {
    const updated = [...interventions];
    updated[index][field] = value;
    setInterventions(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!studentId) {
      setError("Please select a target student.");
      return;
    }

    if (violationType === "OTHER" && !customViolationType.trim()) {
      setError("Please specify details for the custom violation.");
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      student: studentId,
      classroom: classId,
      subject_assignment: subjectAssignmentId || null,
      date_of_incident: dateOfIncident,
      time_of_incident: timeOfIncident,
      location: location,
      infraction_level: infractionLevel,
      violation_type: violationType,
      custom_violation_type: violationType === "OTHER" ? customViolationType : "",
      incident_description: incidentDescription,
      interventions: interventions.map((item) => ({
        intervention_type: item.intervention_type,
        date_attempted: item.date_attempted || null,
        other_details:
          item.intervention_type === "OTHER" ? item.other_details : "",
      })),
    };

    try {
      if (isEditMode) {
        await api.patch(`discipline-referrals/${selectedRecord.id}/`, payload);
      } else {
        await api.post("discipline-referrals/", payload);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Failed to save referral:", err);
      const backendErrors = err.response?.data;
      if (typeof backendErrors === "object") {
        const firstErrorKey = Object.keys(backendErrors)[0];
        const firstErrorMsg = Array.isArray(backendErrors[firstErrorKey])
          ? backendErrors[firstErrorKey][0]
          : backendErrors[firstErrorKey];
        setError(`${firstErrorKey}: ${firstErrorMsg}`);
      } else {
        setError(
          err.response?.data?.detail || "Failed to save disciplinary referral."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white border border-zinc-200 rounded-lg shadow-xl w-full max-w-2xl my-auto overflow-hidden text-xs flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-600" />
            <div>
              <h3 className="font-bold text-zinc-900 text-sm">
                {isEditMode ? "Edit Disciplinary Referral" : "Disciplinary Referral Form"}
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-200/60 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Form Body */}
        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto space-y-4 flex-1">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg flex items-center gap-2 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Target Student Searchable Selection */}
          <StudentSearchSelector
            students={students}
            selectedStudentId={studentId}
            onSelectStudent={handleSelectStudent}
            fetchingStudents={fetchingStudents}
            disabled={isEditMode}
          />

          {/* Incident Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block font-medium text-zinc-700 mb-1 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-zinc-400" /> Date of Incident
              </label>
              <input
                type="date"
                required
                value={dateOfIncident}
                onChange={(e) => setDateOfIncident(e.target.value)}
                className="w-full h-8 border border-zinc-300 rounded px-2 text-xs focus:outline-none focus:border-blue-500 bg-white"
              />
            </div>

            <div>
              <label className="block font-medium text-zinc-700 mb-1 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-zinc-400" /> Time of Incident
              </label>
              <input
                type="time"
                required
                value={timeOfIncident}
                onChange={(e) => setTimeOfIncident(e.target.value)}
                className="w-full h-8 border border-zinc-300 rounded px-2 text-xs focus:outline-none focus:border-blue-500 bg-white"
              />
            </div>

            <div>
              <label className="block font-medium text-zinc-700 mb-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-zinc-400" /> Location
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Classroom, Field, Hallway"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full h-8 border border-zinc-300 rounded px-2 text-xs focus:outline-none focus:border-blue-500 bg-white"
              />
            </div>
          </div>

          {/* Violation Type Dropdown & Custom Violation Input */}
          <div className="space-y-2">
            <label className="block font-medium text-zinc-700 flex items-center gap-1">
              <Tag className="w-3.5 h-3.5 text-zinc-400" /> Violation Type
            </label>
            <CustomDropdown
              options={VIOLATION_TYPES}
              value={violationType}
              onChange={(val) => {
                setViolationType(val);
                if (val !== "OTHER") {
                  setCustomViolationType("");
                }
              }}
              placeholder="Select Violation Type..."
            />

            {violationType === "OTHER" && (
              <input
                type="text"
                required
                placeholder="Specify custom violation details..."
                value={customViolationType}
                onChange={(e) => setCustomViolationType(e.target.value)}
                className="w-full h-8 border border-zinc-300 rounded px-2 text-xs bg-white focus:outline-none focus:border-blue-500 transition-all"
              />
            )}
          </div>

          {/* Section I: Infraction Level */}
          <div className="space-y-1">
            <label className="block font-medium text-zinc-700">
              Infraction Level
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {INFRACTION_LEVELS.map((level) => (
                <label
                  key={level.value}
                  className={`p-2.5 border rounded-lg flex items-center gap-2 cursor-pointer transition-colors ${
                    infractionLevel === level.value
                      ? "border-blue-500 bg-blue-50/50 text-blue-900 font-semibold"
                      : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="infraction_level"
                    value={level.value}
                    checked={infractionLevel === level.value}
                    onChange={(e) => setInfractionLevel(e.target.value)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span>{level.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Section II: Classroom Interventions Attempted */}
          <div className="space-y-2 border-t border-zinc-200 pt-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-zinc-800">
                Classroom Interventions Attempted
              </span>
              <button
                type="button"
                onClick={handleAddIntervention}
                className="text-[#002855] font-mono hover:text-blue-900 font-semibold flex items-center gap-1 text-[11px] cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" /> Add Intervention
              </button>
            </div>

            {interventions.map((item, index) => (
              <div
                key={index}
                className="p-2.5 border border-zinc-200 rounded-lg bg-zinc-50/50 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <CustomDropdown
                    options={INTERVENTION_TYPES}
                    value={item.intervention_type}
                    onChange={(val) =>
                      handleInterventionChange(index, "intervention_type", val)
                    }
                    className="flex-1"
                  />

                  <input
                    type="date"
                    value={item.date_attempted}
                    onChange={(e) =>
                      handleInterventionChange(
                        index,
                        "date_attempted",
                        e.target.value
                      )
                    }
                    className="h-8 border border-zinc-300 rounded px-2 text-xs bg-white focus:outline-none focus:border-blue-500"
                  />

                  {interventions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveIntervention(index)}
                      className="p-1.5 text-rose-500 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {item.intervention_type === "OTHER" && (
                  <input
                    type="text"
                    placeholder="Specify details for 'Other'..."
                    value={item.other_details}
                    onChange={(e) =>
                      handleInterventionChange(
                        index,
                        "other_details",
                        e.target.value
                      )
                    }
                    className="w-full h-8 border border-zinc-300 rounded px-2 text-xs bg-white focus:outline-none focus:border-blue-500"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Section III: Incident Description */}
          <div className="space-y-1 border-t border-zinc-200 pt-3">
            <label className="block font-bold text-zinc-800">
              Incident Description
            </label>
            <p className="text-[11px] text-zinc-500">
              Provide a factual, objective narrative of the behavior.
            </p>
            <textarea
              required
              rows={4}
              value={incidentDescription}
              onChange={(e) => setIncidentDescription(e.target.value)}
              placeholder="Describe what occurred, context, and immediate resolution..."
              className="w-full border border-zinc-300 rounded-lg p-2.5 text-xs text-zinc-800 focus:outline-none focus:border-blue-500 bg-white"
            />
          </div>

          {/* Submit Controls */}
          <div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="h-8 px-4 border border-zinc-300 text-zinc-700 hover:bg-zinc-100 rounded-xl font-mono text-xs font-semibold transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="h-8 px-4 bg-[#002855] hover:bg-blue-900 text-white rounded-xl font-mono text-xs font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
            >
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                ? "Update Record"
                : "Save Record"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DisciplinaryReferralModal;